<template lang="pug">
.reservation-preorder-detail.neutral300
  template(v-if="isLoading")
    v-container.pa-4(fluid)
      v-row(no-gutters)
        v-col
          v-row
            v-col(cols="1")
              v-card.round-xl(outlined link width="36" height="36" @click="onClickBack")
                div.d-flex.align-center.justify-center.fill-height
                  v-icon(color="primary") mdi-arrow-left
            v-col
              v-skeleton-loader(type="article@5")
  template(v-else-if="hasError")
    v-container.pa-4.d-flex.flex-column.flex-grow-1.align-center.justify-center(fluid)
      div.mb-5.text-xl.font-semibold.neutral700--text {{ typeLabel }}
      v-icon(size="48" color="neutral400") $yzi-alert-circle
      div.text-md.font-medium.neutral700--text.mt-4 {{ mode === 'view' ? 'Failed to load' : 'Failed to create' }} {{ type }}
      div.text-sm.neutral500--text.mt-1 {{ errorMessage }}
      y-button.mt-4(label="Go back" color="neutral700" size="sm" dark, @click="onClickBack")
  template(v-else)
    //- header
    v-row.px-6.py-3.neutral100(no-gutters)
      v-col(cols="12")
        div.d-flex.align-center
          v-card.round-xl(outlined link width="36" height="36" @click="onClickBack")
            div.d-flex.align-center.justify-center.fill-height
              v-icon(color="primary") mdi-arrow-left
          div.text-xl.font-semibold.neutral700--text.ml-4 {{ title }}
          yz-badge.ml-3(v-if="isCancelled" size="md" color="red-100" text-color="red-500") Cancelled
    v-divider
    //- body
    v-row.pa-4(no-gutters)
      //- left panel
      v-col.pr-2(cols="4")
        div.parent-scroll(:style="{ '--page-height': pageHeight }")
          div.child-scroll(:style="{ '--page-height': pageHeight, '--nve-right-margin': nveRightMargin }")
            left-panel-reservation-preorder
      //- middle panel
      v-col.px-2(cols="5")
        div.parent-scroll(:style="{ '--page-height': pageHeight }")
          div.child-scroll(:style="{ '--page-height': pageHeight, '--nve-right-margin': nveRightMargin }")
            middle-panel-reservation-preorder(:loading="isRefreshingTreatments" :is-cancelled="isCancelled" :type="type" @updated="onTreatmentsUpdated")
      //- right panel
      v-col.pl-2(cols="3")
        div.parent-scroll(:style="{ '--page-height': pageHeight }")
          div.child-scroll(:style="{ '--page-height': pageHeight, '--nve-right-margin': nveRightMargin }")
            right-panel-reservation-preorder(:type="type" :mode="mode" :reservation-data="reservationData" @completed="onCompleted" @cancelled="onCancelled" @proceed-refund="onProceedRefund")
  v-overlay(:value="isNavigating" opacity="0.3")
    div.d-flex.flex-column.align-center
      v-progress-circular(indeterminate size="48" color="primary")
      div.mt-4.text-md.font-medium Navigating...
  register-patient-dialog(v-if="showRegisterDialog" :open.sync="showRegisterDialog" :existing-patient="reservationData.patient.id" :reservation-fulfill-item="reservationFulfillItem" @updated="onRegistrationCompleted" @closed="showRegisterDialog = false")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import LeftPanelReservationPreorder from '@/components/clinic/reservation-preorder/LeftPanelReservationPreorder.vue'
import MiddlePanelReservationPreorder from '@/components/clinic/reservation-preorder/MiddlePanelReservationPreorder.vue'
import RightPanelReservationPreorder from '@/components/clinic/reservation-preorder/RightPanelReservationPreorder.vue'
import RegisterPatientDialog from '@/components/clinic/registration/RegisterPatientDialog.vue'

export default {
  components: { LeftPanelReservationPreorder, MiddlePanelReservationPreorder, RightPanelReservationPreorder, RegisterPatientDialog },
  props: {
    type: {
      type: String,
      default: 'reservation'
    },
    mode: {
      type: String,
      default: 'create'
    },
    patientId: {
      type: String,
      default: null
    },
    reservationId: {
      type: String,
      default: null
    },
    origin: {
      type: String,
      default: null
    },
    originData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isLoading: true,
      hasError: false,
      errorMessage: '',
      reservationData: null,
      isRefreshingTreatments: false,
      isNavigating: false,
      showRegisterDialog: false,
    }
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'reservationPreorder/getLeadPid',
      invoice: 'reservationPreorder/getInvoice',
      displaySize: 'ui/getDisplaySize',
      scrollbarWidth: 'ui/getScrollbarWidth',
    }),
    isCancelled () {
      return this.reservationData?.status === 'cancelled'
    },
    typeLabel () {
      return this.type === 'reservation' ? 'Reservation' : 'Pre-order'
    },
    title () {
      if (!this.reservationData) return `New ${this.typeLabel}`
      return `${this.typeLabel} #${this.reservationData.rid}`
    },
    endpoint () {
      return this.type === 'reservation'
        ? `/clinics/${this.company.slug}/reservations/`
        : `/clinics/${this.company.slug}/preorders/`
    },
    screenHeight () {
      return window.innerHeight
    },
    pageHeight () {
      let minusHeight = 168
      switch (this.displaySize) {
        case 'xx-small':
          return `${(this.screenHeight / 0.67) - minusHeight}px`
        case 'x-small':
          return `${(this.screenHeight / 0.75) - minusHeight}px`
        case 'small':
          return `${(this.screenHeight / 0.8) - minusHeight}px`
        case 'medium':
          return `${(this.screenHeight / 0.9) - minusHeight}px`
        case 'large':
          return `${(this.screenHeight) - minusHeight}px`
        case 'x-large':
          return `${(this.screenHeight / 1.1) - minusHeight}px`
        default:
          return `${(this.screenHeight) - minusHeight}px`
      }
    },
    nveRightMargin () {
      return `${(-10 - this.scrollbarWidth)}px`
    },
    reservationFulfillItem () {
      if (!this.reservationData) return null
      return { id: this.reservationData.id, type: this.type === 'reservation' ? 'reservation' : 'preorder' }
    },
  },
  mounted () {
    if (this.mode === 'view') {
      this.fetchReservationPreorder()
    } else {
      this.createReservationPreorder()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchReservationPreorder () {
      this.isLoading = true
      try {
        const resp = await this.$axios.get(`${this.endpoint}${this.reservationId}/`)
        this.reservationData = resp.data
        if (resp.data.patient) {
          this.$store.commit('reservationPreorder/setPatient', resp.data.patient)
          this.$store.commit('treatment/setPatient', resp.data.patient)
        }
        const leadPid = resp.data.pid
        if (leadPid) {
          this.$store.commit('reservationPreorder/setLeadPid', leadPid)
          this.$store.commit('treatment/setLeadPid', leadPid)
          await this.fetchInvoice(leadPid)
          this.enrichTreatmentsWithProductType()
        }
      } catch (err) {
        this.hasError = true
        this.errorMessage = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
        this.showMessage(['error', this.errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    async refreshReservationData () {
      try {
        const resp = await this.$axios.get(`${this.endpoint}${this.reservationId}/`)
        this.reservationData = resp.data
        const leadPid = resp.data.pid
        if (leadPid && !this.leadPid) {
          this.$store.commit('reservationPreorder/setLeadPid', leadPid)
          this.$store.commit('treatment/setLeadPid', leadPid)
        }
      } catch {
        // keep existing reservationData on failure
      }
    },
    async createReservationPreorder () {
      this.isLoading = true
      try {
        const resp = await this.$axios.post(this.endpoint, { patient: this.patientId })
        this.reservationData = resp.data
        if (resp.data.patient) {
          this.$store.commit('reservationPreorder/setPatient', resp.data.patient)
          this.$store.commit('treatment/setPatient', resp.data.patient)
        }
        const leadPid = resp.data.pid
        if (leadPid) {
          this.$store.commit('reservationPreorder/setLeadPid', leadPid)
          this.$store.commit('treatment/setLeadPid', leadPid)
          await this.fetchInvoice(leadPid)
        }
        // Replace the /new URL with the view route so a browser refresh loads the existing record
        const createdId = String(resp.data.id)
        if (this.type === 'reservation') {
          this.$router.replace({ name: 'ClinicReservationView', params: { reservationId: createdId, origin: this.origin, data: this.originData } }).catch(() => {})
        } else {
          this.$router.replace({ name: 'ClinicPreorderView', params: { preorderId: createdId, origin: this.origin, data: this.originData } }).catch(() => {})
        }
      } catch (err) {
        this.hasError = true
        this.errorMessage = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
        this.showMessage(['error', this.errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    async fetchInvoice (pid) {
      try {
        const resp = await this.$axios.get(`/invoices/${pid}`)
        this.$store.commit('reservationPreorder/setInvoice', resp.data)
        this.$store.commit('treatment/setInvoice', resp.data)
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    async onCancelled () {
      await this.refreshReservationData()
    },
    async onClickBack () {
      let name = 'ClinicRegistration'
      if (this.origin) name = this.origin
      if (name === 'ClinicCheckout' && this.originData?.params?.pid) {
        this.isNavigating = true
        await this.$nextTick()
        window.location.href = `/admin/clinic/checkout/${this.originData.params.pid}`
        return
      }
      let next = { name }
      if (this.originData && Object.keys(this.originData).length) {
        next = { name, ...this.originData }
      }
      this.$router.push(next).catch(() => {})
    },
    enrichTreatmentsWithProductType () {
      if (!this.reservationData) return
      const treatments = this.$store.getters['reservationPreorder/getTreatments'] || []
      const resvProducts = this.reservationData.products || []
      const enriched = treatments.map(t => {
        if (t.product) {
          const match = resvProducts.find(rp => rp.product?.id === t.product?.id)
          if (match?.product) {
            let updated = t
            const updates = {}
            if (!t.product.type && match.product.type) updates.type = match.product.type
            if (!t.product.medication && match.product.medication) updates.medication = match.product.medication
            if (Object.keys(updates).length) {
              updated = { ...updated, product: { ...updated.product, ...updates } }
            }
            if (updated.bundles && match.bundles) {
              const enrichedBundles = updated.bundles.map(b => {
                if (b.product && !b.product.medication) {
                  const childMatch = match.bundles.find(mb => mb.product?.id === b.product?.id)
                  if (childMatch?.product?.medication) {
                    return { ...b, product: { ...b.product, medication: childMatch.product.medication } }
                  }
                }
                return b
              })
              updated = { ...updated, bundles: enrichedBundles }
            }
            return updated
          }
        }
        return t
      })
      this.$store.commit('reservationPreorder/setTreatments', enriched)
      this.$store.commit('treatment/setTreatments', enriched)
    },
    async onTreatmentsUpdated () {
      this.isRefreshingTreatments = true
      try {
        await this.fetchInvoice(this.leadPid)
        await this.refreshReservationData()
        this.enrichTreatmentsWithProductType()
      } finally {
        this.isRefreshingTreatments = false
      }
    },
    onCompleted () {
      this.showRegisterDialog = true
    },
    onRegistrationCompleted () {
      this.onClickBack()
    },
    onProceedRefund () {
      // Stay on current page for refund processing
    },
  },
}
</script>

<style lang="scss" scoped>
.reservation-preorder-detail {
  min-height: calc(100vh - 72px);
  display: flex;
  flex-direction: column;
  > .container {
    flex: 1;
  }
}
.parent-scroll {
  overflow: hidden;
  height: var(--page-height) !important;
}
.child-scroll {
  overflow-y: scroll;
  height: var(--page-height) !important;
  margin-right: var(--nve-right-margin);
  padding-right: 10px;
}
</style>
